import React from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/AuthContext'
import { sdk } from '@/lib/sdk'
import type { TwoFactorChallenge } from '@/lib/sdk-official/resources/two-factor'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { showFlashToast } from '@/lib/flashToast'
import { TwoFactorCodeForm } from './TwoFactorCodeForm'

interface TwoFactorLoginStepProps {
  email: string
  challenge: TwoFactorChallenge
  onDone: () => void
  /** Back to the password (also after the challenge expired). */
  onBack: () => void
}

/**
 * F-233: the login's second step. The code went to WhatsApp (or SMS); the
 * person types it, or asks for SMS, or uses a recovery code. "Lembrar este
 * dispositivo por 90 dias" keeps this browser from asking again. The MCP's
 * OAuth consent signs in through this same dialog.
 */
export const TwoFactorLoginStep = ({ email, challenge, onDone, onBack }: TwoFactorLoginStepProps) => {
  const { t } = useTranslation('twoFactor')
  const { completeTwoFactorLogin } = useAuth()
  const [remember, setRemember] = React.useState(false)
  const [expired, setExpired] = React.useState<string | null>(null)

  const notice = React.useMemo(() => {
    if (!challenge.codeError) return null
    return { text: challenge.codeError.message, suggestRecovery: challenge.codeError.status === 429 }
  }, [challenge.codeError])

  const submit = async (answer: { code?: string; recoveryCode?: string }) => {
    try {
      const result = await completeTwoFactorLogin(email, { challengeId: challenge.challengeId, ...answer, rememberDevice: remember })
      /* The session starts here and the dialog closes with it: how many codes are left goes in the
         flash notice, which outlives it (the e-mail alert says the same). */
      if (result.recoveryCodeUsed) showFlashToast(t('login.recoveryUsed', { count: result.recoveryCodesRemaining ?? 0 }))
      onDone()
    } catch (err) {
      /* The challenge is over (15 minutes, or too many wrong codes): log in again. */
      if (apiErrorMessage(err).param === 'challengeId') {
        setExpired(apiErrorMessage(err).message || t('login.expired'))
        return
      }
      throw err
    }
  }

  if (expired) {
    return (
      <div className="space-y-4">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground"><ShieldCheck className="h-5 w-5 flex-shrink-0 text-primary" />{t('login.title')}</h2>
        <p role="alert" className="rounded-lg bg-danger-surface p-3 text-sm text-danger-text">{expired}</p>
        <Button type="button" variant="secondary" className="w-full" onClick={onBack}>{t('login.again')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-5" data-testid="two-factor-login-step">
      <div>
        <h2 className="mb-2 flex items-center gap-2 text-xl font-semibold text-foreground"><ShieldCheck className="h-5 w-5 flex-shrink-0 text-primary" />{t('login.title')}</h2>
        <p className="text-base text-foreground-secondary">{t('login.description')}</p>
      </div>
      <TwoFactorCodeForm
        delivery={{ phone: challenge.phone, sentVia: challenge.sentVia, resendAfterSeconds: challenge.resendAfterSeconds ?? challenge.codeError?.retryAfterSeconds }}
        notice={notice}
        onSubmit={submit}
        onResend={(channel) => sdk.twoFactor.resendLogin(challenge.challengeId, channel)}
        submitLabel={t('login.submit')}
        testId="two-factor-login-form"
        extra={(
          <label className="flex items-start gap-2 text-sm text-foreground-secondary">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border text-primary" data-testid="two-factor-remember" />
            <span>{t('login.remember')}</span>
          </label>
        )}
        secondary={<Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onBack}>{t('login.back')}</Button>}
      />
    </div>
  )
}
